import { canBookSessions, isSubscriptionVisible } from '@shared/utils/subscription-status';

export type BillingInterval = 'monthly' | 'annual';

export interface PlanPrice {
    /** Betrag in Cent (Stripe unit_amount kann null sein). */
    amount: number | null;
    currency: string;
}

export interface SubscriptionPlanView {
    id: string;
    name: string;
    includedMinutes: number;
    discountPercent: number;
    /** Freie Event-Starts pro Monat; optional, weil ältere Server-Antworten das Feld nicht führen. */
    eventQuota?: number;
    sortOrder: number;
    monthly: PlanPrice;
    annual: PlanPrice;
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | (string & {});

export interface SubscriptionInfo {
    plan_id: string;
    discount_percent: number;
    status: SubscriptionStatus;
    billing_interval: BillingInterval;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    /** ISO-8601, z. B. "2026-12-01T00:00:00Z". */
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    player_id: number | null;
    /**
     * Ende der laufenden Minuten-Scheibe (ISO-8601). Freiminuten laufen im Monatsrhythmus,
     * auch bei Jahresabos — deshalb ist das nicht `current_period_end`. Optional, weil
     * ältere Server-/n8n-Stände das Feld nicht liefern (Fallback: `current_period_end`).
     */
    minutes_period_end?: string | null;
    /** Tarif, zu dem zum Periodenende gewechselt wird (Downgrade/Jahr→Monat); null ohne Vormerkung. */
    pending_plan_id?: string | null;
    pending_interval?: BillingInterval | null;
    /** Wirksamkeitszeitpunkt der Vormerkung (ISO-8601). */
    pending_change_at?: string | null;
}

/** Ein zum Periodenende vorgemerkter Tarifwechsel — nur vollständige Vormerkungen zählen. */
export function pendingPlanChange(sub: SubscriptionInfo | null): { planId: string; changeAt: string } | null {
    if (!sub?.pending_plan_id || !sub.pending_change_at) return null;
    return { planId: sub.pending_plan_id, changeAt: sub.pending_change_at };
}

export interface SubscriptionMe {
    subscription: SubscriptionInfo | null;
    remainingMinutes: number;
}

/**
 * Zeigbares Abo: Kachel und Mitgliedschafts-Tab statt Werbe-Leerzustand. Schließt `past_due`
 * und `unpaid` bewusst ein — dort muss der Kunde seine Zahlung sehen und reparieren können.
 * Sagt NICHTS darüber aus, ob gebucht werden darf: dafür `canBookSubscription`.
 */
export function hasActiveSubscription(me: SubscriptionMe | null): boolean {
    return isSubscriptionVisible(me?.subscription?.status);
}

/**
 * Darf dieser Kunde Sessions buchen? Deckungsgleich mit `requireActiveSubscription` am Server.
 *
 * Ohne diese Trennung bot das Frontend einem `past_due`-Mitglied den Buchungs-Wizard an,
 * dessen erster Aufruf serverseitig mit 403 endete („Sessions konnten nicht geladen werden").
 */
export function canBookSubscription(me: SubscriptionMe | null): boolean {
    return canBookSessions(me?.subscription?.status);
}

export interface UpgradeOffer {
    plan: SubscriptionPlanView;
    /** Freiminuten pro Monat, die das Upgrade zusätzlich bringt (immer > 0). */
    minutesDiff: number;
}

/**
 * Das nächsthöhere Abo gemessen an `includedMinutes` — das ist die beworbene Größe, deshalb
 * zählt sie und nicht `sortOrder`. Gleiche Minutenzahl ist kein Upgrade. `null`, wenn der
 * aktuelle Plan nicht (mehr) in der Liste steht oder bereits das größte Abo ist.
 */
export function nextUpgradePlan(currentPlanId: string, plans: SubscriptionPlanView[]): UpgradeOffer | null {
    const current = plans.find((p) => p.id === currentPlanId);
    if (!current) return null;
    let best: SubscriptionPlanView | null = null;
    for (const p of plans) {
        if (p.includedMinutes > current.includedMinutes && (!best || p.includedMinutes < best.includedMinutes)) {
            best = p;
        }
    }
    return best ? { plan: best, minutesDiff: best.includedMinutes - current.includedMinutes } : null;
}

/**
 * Ganze Kalendertage von `now` bis zum Ende der Abo-Periode.
 *
 * Bewusst über Kalendertage statt über eine Millisekunden-Division: Letztere hinge an der
 * Tageszeit des Aufrufs (abends käme ein Tag weniger heraus als morgens) und läge über die
 * Sommerzeitumstellung um einen Tag daneben. Die Normalisierung auf `Date.UTC` der lokalen
 * Y/M/D-Anteile legt beide Daten auf eine DST-freie Achse.
 *
 * `null` bei fehlendem oder ungültigem Datum, `0` am selben Kalendertag, negativ in der Vergangenheit.
 */
export function remainingDays(iso: string | null, now: Date = new Date()): number | null {
    if (!iso) return null;
    const end = new Date(iso);
    if (Number.isNaN(end.getTime())) return null;
    const dayStart = (d: Date): number => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.round((dayStart(end) - dayStart(now)) / 86_400_000);
}
